import React, { useRef, useMemo, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { format } from 'date-fns'
import * as Modal from '_components/modal/generic-modal'
import { Grid, Link, Typography, TextField, InputLabel } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { BasicInput, MaskBasicInput, Select } from '_/components/inputs'
import { STATUS_API_OPTIONS } from '../utils/constants'
import { Loader } from '@refera/ui-web'

import { updateErrorCorrection } from '_modules/ai-integration/actions'

import { useDialog } from '_/hooks/use-dialog'
import { useToast } from '_/hooks/use-toast'
import useStyles from './styles'

const AiCorrectionsModal = ({ correction, open, setOpen, reloadCorrections }) => {
  const styles = useStyles()
  const formRef = useRef()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()
  const aiAssistant = correction?.aiAssistant

  const [isLoading, setIsLoading] = useState(false)

  const methods = useForm({
    mode: 'all',
  })

  const { isValid, dirtyFields } = methods.formState

  const creationDate = useMemo(() => {
    if (correction?.createdAt) {
      const newDate = new Date(correction?.createdAt)
      return format(newDate, 'dd/MM/yyyy HH:mm')
    }
    return ''
  }, [correction?.createdAt])

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          setOpen(false)
        },
        onCancel: closeDialog,
      })
    } else {
      setOpen(false)
    }
  }

  const onSubmit = useCallback(
    data => {
      if (!isValid) return

      const formattedData = { statusApi: data?.statusApi }
      setIsLoading(true)
      dispatch(updateErrorCorrection(correction?.id, formattedData))
        .then(() => {
          showToast({
            type: 'success',
          })
          reloadCorrections()
          setOpen(false)
        })
        .catch(() => {
          showToast({
            type: 'error',
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [dispatch, isValid, correction?.id, reloadCorrections]
  )

  return (
    <>
      <Loader open={isLoading} hasBackdrop />
      <form ref={formRef} id="ai-correction">
        <FormProvider {...methods}>
          <Modal.Root className={styles.root} open={open} onClose={() => setOpen(false)}>
            <Modal.TitleModal title="Treinamento de IA" />

            <Modal.Content>
              <Grid className={styles.content}>
                <BasicInput
                  label="Tipo de IA"
                  name="aiAssistantName"
                  defaultValue={aiAssistant?.name}
                  disabled
                  sx={{ gridArea: 'aiAssistantName' }}
                />
                <Grid sx={{ gridArea: 'serviceOrder' }} className={styles.linkContainer}>
                  <Typography className={styles.inputLabel}>Chamado</Typography>
                  <Link
                    underline="always"
                    target="_blank"
                    rel="noopener"
                    className={styles.link}
                    href={`/chamado/${correction?.serviceOrder}/`}
                  >
                    {correction?.serviceOrder}
                  </Link>
                </Grid>

                <Grid sx={{ gridArea: 'explanation', marginTop: -4 }}>
                  <InputLabel variant="body2" id="explanation-label" className={styles.label}>
                    Explicação
                  </InputLabel>
                  <TextField
                    name="explanation"
                    value={correction?.explanation}
                    multiline
                    fullWidth
                    variant="outlined"
                    InputProps={{ style: { fontSize: 16, paddingLeft: 10 } }}
                    disabled
                    defaultValue=""
                  />
                </Grid>

                <MaskBasicInput
                  name="createdAt"
                  label="Criado em:"
                  disabled
                  defaultValue={creationDate}
                  sx={{ gridArea: 'createdAt' }}
                />

                <BasicInput
                  label="Usuário"
                  name="userName"
                  defaultValue={correction?.userName}
                  disabled
                  sx={{ gridArea: 'userName' }}
                />

                {aiAssistant?.isActive && (
                  <Select
                    label="Status API"
                    name="statusApi"
                    required
                    defaultValue={correction?.statusApi}
                    options={STATUS_API_OPTIONS}
                    getValue={item => item?.value}
                    getLabel={item => item?.label}
                    disabled={correction?.statusApi === 'processed'}
                  />
                )}
              </Grid>
            </Modal.Content>

            <Modal.Actions>
              <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
              <Modal.ButtonFullBlue form="ai-correction" onClick={methods.handleSubmit(onSubmit)}>
                salvar
              </Modal.ButtonFullBlue>
            </Modal.Actions>
          </Modal.Root>
        </FormProvider>
      </form>
    </>
  )
}

export default AiCorrectionsModal
